import { useAppContext } from '../../../context/AppContext';
import { postData, trackProjectCreated } from '../../../services';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
    COUNTRIES,
    applyCountryChange,
    getPostalCodeLabel,
    getRegionSelectOptions,
    getStateProvinceLabel,
    resolveCountryCode,
} from '../../../context/usStates';
import { DesignIntakeFormBody } from '../designs/DesignIntakeForm';
import { createDesignFromIntake } from '../../../utils/designIntake';

const PROJECT_STEPS = 2;
const DESIGN_STEPS = 6;
const TOTAL_STEPS = PROJECT_STEPS + DESIGN_STEPS;
const STAGE_1 = 1;
const STAGE_2 = 2;
const LOCATION_ADDRESS = 'address';
const LOCATION_GPS = 'gps';
const POWER_UNIT_KW = 'kW';
const POWER_UNIT_MW = 'MW';

export const NewProjectForm = ({ companyId } = {}) => {
    const { token, base_url, setProjects, user, company } = useAppContext();
    const navigate = useNavigate();
    const [stage, setStage] = useState(STAGE_1);
    const [locationType, setLocationType] = useState(LOCATION_ADDRESS);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        projectType: 'Portal',
        totalPowerAmount: '',
        powerUnit: POWER_UNIT_KW,
        site_address_1: '',
        site_address_2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'US',
        gps_lat: '',
        gps_lng: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasReachedDesignSteps, setHasReachedDesignSteps] = useState(false);

    const projectTypes = [
        'Agrivoltaics',
        'Carport',
        'Luxury Home / Cabin',
        'Commercial',
        'Residential',
        'DIY',
        'Canopy / Cover',
        'Megawatt',
        'Telecom',
        'Rail Part',
        'Parts'
    ];

    const isStage1Valid = formData.name.trim() !== '' && formData.projectType.trim() !== '';
    const isProjectStage = stage <= PROJECT_STEPS;

    const actingCompanyId =
        user?.is_super_admin === true && companyId != null && Number.isFinite(Number(companyId))
            ? Number(companyId)
            : null;

    const buildProjectPayload = () => {
        const totalPowerTrimmed = formData.totalPowerAmount.trim();
        return {
            project_name: formData.name,
            description: formData.description,
            project_type: formData.projectType,
            total_power_generated:
                totalPowerTrimmed !== '' ? `${totalPowerTrimmed} ${formData.powerUnit}` : '',
            ...(actingCompanyId != null ? { company_id: actingCompanyId } : {}),
            ...(locationType === LOCATION_GPS
                ? {
                    site_address_1: formData.gps_lat,
                    site_address_2: formData.gps_lng,
                    city: ' ',
                    state: ' ',
                    zip_code: ' '
                }
                : {
                    site_address_1: formData.site_address_1,
                    site_address_2: formData.site_address_2,
                    city: formData.city,
                    state: formData.state,
                    zip_code: formData.zip_code,
                    country: formData.country
                })
        };
    };

    const handleNext = (e) => {
        e.preventDefault();
        if (stage === STAGE_1 && isStage1Valid) {
            setStage(STAGE_2);
        } else if (stage === STAGE_2) {
            setHasReachedDesignSteps(true);
            setStage(PROJECT_STEPS + 1);
        }
    };

    const handleBackToStage1 = (e) => {
        e.preventDefault();
        setStage(STAGE_1);
    };

    const handleCancel = () => {
        if (actingCompanyId != null) {
            navigate(`/super-admin?companyId=${actingCompanyId}&tab=projects`);
            return;
        }
        navigate(-1);
    };

    const handleCreateProjectAndDesign = async ({ formData: designFormData, runAutoDesigner }) => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const projectPayload = buildProjectPayload();
            const response = await postData(token, `${base_url}/apis/projects/create`, projectPayload);

            if (response?.status && response.status >= 400) {
                throw new Error(response?.msg || response?.error || 'Failed to create project. Please try again.');
            }

            if (!response?.project) {
                throw new Error(response?.error || response?.msg || 'Failed to create project. Please try again.');
            }

            const createdProject = response.project;
            setProjects(prevProjects => [...prevProjects, createdProject]);
            trackProjectCreated({
                companyId: createdProject.company_id || actingCompanyId || company?.id || user?.company_id,
                projectId: createdProject.id,
                projectType: formData.projectType,
                mountSeries: designFormData?.product_type,
            });

            await createDesignFromIntake({
                token,
                base_url,
                project: createdProject,
                formData: designFormData,
                runAutoDesigner,
                userEmail: user?.email,
            });

            navigate(`/project/${createdProject.id}?tab=designs`);
        } catch (error) {
            console.error('Error creating project and design:', error);
            const statusCode = error?.status || 500;
            const message = error?.message || error?.msg || 'Something went wrong. Please try again.';
            window.showAlert(statusCode, message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => (
            name === 'country' ? applyCountryChange(prev, value) : { ...prev, [name]: value }
        ));
    };

    const siteCountry = resolveCountryCode(formData.country, formData.state);
    const siteRegions = getRegionSelectOptions(siteCountry, formData.state);
    const siteStateLabel = getStateProvinceLabel(siteCountry);
    const sitePostalLabel = getPostalCodeLabel(siteCountry);

    return (
        <div>
            {isProjectStage && (
                <>
                    <div className="form-step-indicator">
                        Step {stage} of {TOTAL_STEPS}
                    </div>
                    <form onSubmit={handleNext}>
                        {stage === STAGE_1 && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="name" className="form-label">Project Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="form-input"
                                        placeholder="Enter project name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="projectType" className="form-label">Project Type</label>
                                    <select
                                        id="projectType"
                                        name="projectType"
                                        value={formData.projectType}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        {projectTypes.map((type) => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="totalPowerAmount" className="form-label">
                                        how much power will your solar project generate?
                                    </label>
                                    <div className="form-row-inline">
                                        <input
                                            type="text"
                                            id="totalPowerAmount"
                                            name="totalPowerAmount"
                                            value={formData.totalPowerAmount}
                                            onChange={handleChange}
                                            className="form-input form-input--flex"
                                            placeholder="Amount"
                                            inputMode="decimal"
                                            autoComplete="off"
                                        />
                                        <div className="radio-group">
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="powerUnit"
                                                    checked={formData.powerUnit === POWER_UNIT_KW}
                                                    onChange={() => setFormData(prev => ({ ...prev, powerUnit: POWER_UNIT_KW }))}
                                                />
                                                Kilowatt (kW)
                                            </label>
                                            <label className="radio-label">
                                                <input
                                                    type="radio"
                                                    name="powerUnit"
                                                    checked={formData.powerUnit === POWER_UNIT_MW}
                                                    onChange={() => setFormData(prev => ({ ...prev, powerUnit: POWER_UNIT_MW }))}
                                                />
                                                Megawatt (MW)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="description" className="form-label">Project Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="2"
                                        className="form-textarea"
                                        placeholder="Describe your project..."
                                    />
                                </div>
                            </>
                        )}

                        {stage === STAGE_2 && (
                            <>
                                <div className="form-group">
                                    <label className="form-label">Location</label>
                                    <div className="radio-group">
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="locationType"
                                                checked={locationType === LOCATION_ADDRESS}
                                                onChange={() => setLocationType(LOCATION_ADDRESS)}
                                            />
                                            Address
                                        </label>
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="locationType"
                                                checked={locationType === LOCATION_GPS}
                                                onChange={() => setLocationType(LOCATION_GPS)}
                                            />
                                            GPS coordinates
                                        </label>
                                    </div>
                                </div>

                                {locationType === LOCATION_ADDRESS && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="site_address_1" className="form-label">Site Address</label>
                                            <input
                                                type="text"
                                                id="site_address_1"
                                                name="site_address_1"
                                                value={formData.site_address_1}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="Address line 1"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="site_address_2" className="form-label">Site Address (continued)</label>
                                            <input
                                                type="text"
                                                id="site_address_2"
                                                name="site_address_2"
                                                value={formData.site_address_2}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="Address line 2"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="country" className="form-label">Country</label>
                                            <select
                                                id="country"
                                                name="country"
                                                value={siteCountry}
                                                onChange={handleChange}
                                                className="form-select"
                                            >
                                                {COUNTRIES.map((country) => (
                                                    <option key={country.code} value={country.code}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group form-group--city-row">
                                            <label htmlFor="city" className="form-label form-label--full-width">
                                                City, {siteStateLabel}, {sitePostalLabel}
                                            </label>
                                            <input
                                                type="text"
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                className="form-input form-input--city"
                                                placeholder="City"
                                            />
                                            <select
                                                id="state"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                className="form-select form-input--state"
                                            >
                                                <option value="">{siteStateLabel}</option>
                                                {siteRegions.map((region) => (
                                                    <option key={region.abbr} value={region.abbr}>{region.name}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="text"
                                                id="zip_code"
                                                name="zip_code"
                                                value={formData.zip_code}
                                                onChange={handleChange}
                                                className="form-input form-input--zip"
                                                placeholder={siteCountry === 'CA' ? 'A1A 1A1' : 'Zip'}
                                            />
                                        </div>
                                    </>
                                )}

                                {locationType === LOCATION_GPS && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="gps_lat" className="form-label">Latitude</label>
                                            <input
                                                type="text"
                                                id="gps_lat"
                                                name="gps_lat"
                                                value={formData.gps_lat}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="e.g. 39.7392"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="gps_lng" className="form-label">Longitude</label>
                                            <input
                                                type="text"
                                                id="gps_lng"
                                                name="gps_lng"
                                                value={formData.gps_lng}
                                                onChange={handleChange}
                                                className="form-input"
                                                placeholder="e.g. -104.9903"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <div className="form-actions">
                            {stage === STAGE_1 && (
                                <>
                                    <button type="button" onClick={handleCancel} className="btn btn-secondary">
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={!isStage1Valid}>
                                        Next
                                    </button>
                                </>
                            )}
                            {stage === STAGE_2 && (
                                <>
                                    <button type="button" onClick={handleBackToStage1} className="btn btn-secondary">
                                        Back
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        Next
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </>
            )}

            {hasReachedDesignSteps && (
                <div className={isProjectStage ? 'form-section--hidden' : undefined}>
                    <DesignIntakeFormBody
                        embedded
                        initialDesignName={formData.name}
                        controlledStepIndex={stage - PROJECT_STEPS - 1}
                        onControlledStepIndexChange={(index) => setStage(index + PROJECT_STEPS + 1)}
                        onBackFromFirstStep={() => setStage(STAGE_2)}
                        stepDisplayNumber={stage}
                        totalStepCount={TOTAL_STEPS}
                        onCustomSubmit={handleCreateProjectAndDesign}
                        submitButtonTextOverride="Create Project & Design"
                        onClose={handleCancel}
                    />
                </div>
            )}
        </div>
    );
};
